import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../Common/Configurations/AuthContext';
import Header from '../../Common/Layout/Header';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import BookingItem from './BookingItem';

import {
    FaPlane, FaHotel, FaUmbrellaBeach, FaUsers, FaBuilding, 
    FaSyncAlt, FaEye, FaCalendarAlt, FaHandshake, FaCheckCircle, 
    FaUserFriends
} from 'react-icons/fa';

import { Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, 
    Title, Tooltip, Legend, Filler, ArcElement
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ArcElement);

const initialDashboardData = {
    totalBranches: 0,
    branches: [],
    counts: {
        flights: {}, hotels: {}, holidays: {}, leads: {}, customers: {}
    },
    recentFlights: [],
    recentHotels: []
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const [dashboardData, setDashboardData] = useState(initialDashboardData);
    const [loading, setLoading] = useState(true);
    const [selectedBranch, setSelectedBranch] = useState('all');
    const [timeFilter, setTimeFilter] = useState('this_month');
    const [activeBookingTab, setActiveBookingTab] = useState('flight'); 
    const [distributionType, setDistributionType] = useState('services');

    const getDashboardData = useCallback(async () => {
        if (!user?.cid) return;
        setLoading(true);
        try {
            let resourceUrl = `${config.GETDASHBOARD}/${user.cid}?dateRange=${timeFilter}`;
            if (selectedBranch !== 'all') {
                resourceUrl += `&branchId=${selectedBranch}`;
            }
            const response = await apiService.getAll(resourceUrl);
            setDashboardData(response || initialDashboardData); // Ensure data is never null
        } catch (error) {
            console.error("Failed to fetch dashboard data:", error);
            setDashboardData(initialDashboardData);
        } finally {
            setLoading(false);
        }
    }, [user, selectedBranch, timeFilter]);
    
    useEffect(() => {
        if (!authLoading && user) {
            getDashboardData();
        } else if (!authLoading && !user) {
            navigate("/");
        }
    }, [authLoading, user, navigate, getDashboardData]);
    
    // Memoize the list of recent bookings to filter efficiently
    const visibleBookings = useMemo(() => {
        const flights = dashboardData.recentFlights || [];
        const hotels = dashboardData.recentHotels || [];
        const allBookings = [...flights, ...hotels].sort((a, b) => new Date(b.bookingDate) - new Date(a.bookingDate));

        if (activeBookingTab === 'all') return allBookings;
        return allBookings.filter(b => b.bookingType.toLowerCase() === activeBookingTab);
    }, [dashboardData, activeBookingTab]);
    
    // Static chart data (can be made dynamic later)
    const chartConfig = {
        line: {
            data: {
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
                datasets: [{
                    label: 'Bookings', data: [65, 59, 80, 81, 56, 55, 90],
                    borderColor: 'hsl(221, 83%, 53%)', backgroundColor: 'hsla(221, 83%, 53%, 0.1)',
                    fill: true, tension: 0.4,
                }]
            },
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true, grid: { drawBorder: false } }, x: { grid: { display: false } } }
            }
        },
        doughnut: {
            data: {
                services: {
                    labels: ['Flights', 'Hotels', 'Holidays', 'Sightseeing'],
                    datasets: [{
                        data: [45, 30, 20, 5],
                        backgroundColor: ['#3b82f6', '#8b5cf6', '#10b981', '#f97316'],
                        borderColor: '#ffffff', borderWidth: 4,
                    }]
                },
                branches: {
                    labels: ['Mumbai', 'Delhi', 'Bangalore', 'Pune'],
                    datasets: [{
                        data: [40, 25, 20, 15],
                        backgroundColor: ['#3b82f6', '#ef4444', '#10b981', '#f97316'],
                        borderColor: '#ffffff', borderWidth: 4,
                    }]
                }
            },
            options: {
                responsive: true, maintainAspectRatio: false, cutout: '70%',
                plugins: { legend: { display: true, position: 'bottom', labels: { usePointStyle: true, pointStyle: 'circle', padding: 20, boxWidth: 8, font: { size: 14 } } } }
            }
        }
    };
    
    if (authLoading) return <p className="loading-fullscreen">Loading User...</p>;

    const counts = dashboardData.counts;

    return (
        <div id="layout-wrapper" className="dashboard-body">
            <Header />
            <div className="main-content">
                <div className="page-content">
                    <div className="dashboard-container">
                        
                        <div className="dashboard-header">
                            <div className="welcome-text">
                                <h2>Welcome back, {user?.brandName || 'Admin'}! </h2>
                                <p>Here is the pulse of your business operations.</p>
                            </div>
                            <div className="total-branches-widget">
                                <div className="icon-wrapper"><FaBuilding /></div>
                                <div className="text-wrapper">
                                    <span className="value">{loading ? '...' : dashboardData.totalBranches}</span>
                                    <span className="label">Total Branches</span>
                                </div>
                            </div>
                        </div>
                        <div className="dashboard-filters">
                            <div className="filter-group">
                                <label htmlFor="branch-filter"><FaBuilding /></label>
                                <select id="branch-filter" value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)} disabled={loading}>
                                    <option value="all">All Branches</option>
                                    {(dashboardData.branches || []).map(branch => 
                                        <option key={branch.branchId} value={branch.branchId}>{branch.branchName}</option>
                                    )}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label htmlFor="time-filter"><FaCalendarAlt /></label>
                                <select id="time-filter" value={timeFilter} onChange={e => setTimeFilter(e.target.value)} disabled={loading}>
                                    <option value="this_month">This Month</option>
                                    <option value="today">Today</option>
                                    <option value="this_week">This Week</option>
                                    <option value="last_month">Last Month</option>
                                </select>
                            </div>
                            <button className="btn-primary refresh-btn" onClick={getDashboardData} disabled={loading}>
                                <FaSyncAlt className={loading ? 'spinner' : ''} />
                                <span>{loading ? 'Loading...' : 'Refresh'}</span>
                            </button>
                        </div>
                        
                        <div className="summary-grid">
                            <div className="summary-card theme-flights">
                                <div className="card-header"><div className="metric-icon flights"><FaPlane /></div><h3>Flights</h3></div>
                                <div className="card-stats">
                                    <div className="stat-item"><span>Upcoming Departures</span><span className="value">{loading ? '...' : counts.flights.upcomingDepartures || 0}</span></div>
                                    <div className="stat-item"><span>Confirmed Bookings</span><span className="value">{loading ? '...' : counts.flights.confirmedBookings || 0}</span></div>
                                    <div className="stat-item"><span>Unconfirmed</span><span className="value">{loading ? '...' : counts.flights.unconfirmedBookings || 0}</span></div>
                                    <div className="stat-item"><span>Cancelled</span><span className="value">{loading ? '...' : counts.flights.cancelledBookings || 0}</span></div>
                                 </div>
                                <div className="card-footer"><button className="view-btn"  onClick={() => navigate("/flight/bookings")}><FaEye /> View Details</button></div>
                            </div>
                            {/* Other summary cards follow the same pattern... */}
                            <div className="summary-card theme-hotels">
                                <div className="card-header"><div className="metric-icon hotels"><FaHotel /></div><h3>Hotels</h3></div><div className="card-stats"><div className="stat-item"><span>Upcoming Check-ins</span><span className="value">{loading ? '...' : counts.hotels.upcomingCheckIns || 0}</span></div><div className="stat-item"><span>Confirmed Bookings</span><span className="value">{loading ? '...' : counts.hotels.confirmedBookings || 0}</span></div><div className="stat-item"><span>Pending Confirmation</span><span className="value">{loading ? '...' : counts.hotels.pendingConfirmation || 0}</span></div><div className="stat-item"><span>Cancelled</span><span className="value">{loading ? '...' : counts.hotels.cancelledBookings || 0}</span></div></div>
                            <div className="card-footer"><button className="view-btn" onClick={() => navigate("/hotel/bookings")}><FaEye /> View Details</button></div></div>
                            <div className="summary-card theme-packages"><div className="card-header"><div className="metric-icon packages"><FaUmbrellaBeach /></div><h3>Holidays</h3></div>
                            <div className="card-stats"><div className="stat-item"><span>Total Packages</span><span className="value">{loading ? '...' : counts.holidays.totalPackages || 0}</span></div>
                                <div className="stat-item"><span>Active Packages</span><span className="value">{loading ? '...' : counts.holidays.activePackages || 0}</span></div>
                                <div className="stat-item"><span>InActive Packages</span><span className="value">{loading ? '...' : counts.holidays.inActivePackages || 0}</span></div>
                            <div className="stat-item"><span>Total Enquiries</span><span className="value">{loading ? '...' : counts.holidays.totalEnquiries || 0}</span></div></div>
                            <div className="card-footer"><button className="view-btn" onClick={() => navigate("/packageslist")}><FaEye /> View Packages</button></div></div>
                            <div className="summary-card theme-leads"><div className="card-header"><div className="metric-icon leads"><FaHandshake /></div><h3>Leads Status</h3></div><div className="card-stats"><div className="stat-item"><span>New Leads</span><span className="value hot">{loading ? '...' : counts.leads.NEW || 0}</span></div><div className="stat-item"><span>Follow-up Required</span><span className="value warm">{loading ? '...' : counts.leads.followUpRequired || 0}</span></div><div className="stat-item"><span>Converted</span><span className="value green">{loading ? '...' : counts.leads.converted || 0}</span></div><div className="stat-item"><span>Customers</span><span className="value">{loading ? '...' : counts.customers.totalCustomers || 0}</span></div></div>
                            <div className="card-footer"><button className="view-btn" onClick={() => navigate("/leads")}><FaEye /> Manage Leads</button></div></div>
                        </div>

                        <div className="main-content-grid">
                            <div className="glass-card recent-bookings-new">
                                <div className="card-header">
                                    <h3>Recent Bookings</h3>
                                    <div className="tabs-container service-tabs">
                                        {/* <button className={`tab ${activeBookingTab === 'all' ? 'active' : ''}`} onClick={() => setActiveBookingTab('all')}>All</button> */}
                                        <button className={`tab ${activeBookingTab === 'flight' ? 'active' : ''}`} onClick={() => setActiveBookingTab('flight')}><FaPlane/> Flights</button>
                                        <button className={`tab ${activeBookingTab === 'hotel' ? 'active' : ''}`} onClick={() => setActiveBookingTab('hotel')}><FaHotel/> Hotels</button>
                                    </div>
                                </div>
                                <div className="new-bookings-list">
                                    {loading ? (
                                        <p className="loading-placeholder">Loading bookings...</p>
                                    ) : visibleBookings.length > 0 ? (
                                        visibleBookings.map(booking => (
                                            <BookingItem key={`${booking.bookingType}-${booking.bookingId}`} booking={booking} />
                                        ))
                                    ) : (
                                        <p className="loading-placeholder">No recent bookings found.</p>
                                    )}
                                </div>
                            </div>
                            
                            <div className="charts-container">
                                <div className="glass-card booking-distribution">
                                    <div className="card-header">
                                        <h3>Booking Distribution</h3>
                                        <div className="toggle-buttons">
                                            <button className={distributionType === 'services' ? 'active' : ''} onClick={() => setDistributionType('services')}>Services</button>
                                            <button className={distributionType === 'branches' ? 'active' : ''} onClick={() => setDistributionType('branches')}>Branches</button>
                                        </div>
                                    </div>
                                    <div className="doughnut-chart-container">
                                        <Doughnut data={chartConfig.doughnut.data[distributionType]} options={chartConfig.doughnut.options} />
                                    </div>
                                </div>
                                <div className="glass-card booking-trends">
                                    <div className="card-header">
                                        <h3>Booking Trends</h3>
                                        <span className="subtitle">Last 7 Months</span>
                                    </div>
                                    <div className="line-chart-container">
                                        <Line data={chartConfig.line.data} options={chartConfig.line.options} />
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;